// src/pages/CancelRedeem.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css"; // reuse existing styling
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

export default function CancelRedeem() {
  const navigate = useNavigate();
  useEscapeToGoBack();

  const [mmyy, setMmyy] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState("");

  const [confirmCancel, setConfirmCancel] = useState(null);      // first confirm
  const [reconfirmCancel, setReconfirmCancel] = useState(null);  // second confirm
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---- refs for keyboard UX ----
  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    // focus MMYY on mount
    mmyyRef.current?.focus();
    // place caret at end if any prefilled value
    if (mmyyRef.current) {
      const el = mmyyRef.current;
      const len = el.value?.length || 0;
      el.setSelectionRange?.(len, len);
    }
  }, []);

  // ---------- helpers ----------
  const onlyDigits = (s = "") => String(s).replace(/\D/g, "");
  const pad4 = (s = "") => onlyDigits(s).padStart(4, "0");
  const detectSeries = (tailDigits) => (tailDigits.length === 5 ? "S" : "B");

  const formatDateDDMMYYYY = (isoStr) => {
    if (!isoStr) return "-";
    const d = new Date(isoStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const buildCanonicalReceipt = () => {
    const mmYY = pad4(mmyy);
    const tailDigits = onlyDigits(receiptNo);
    const series = detectSeries(tailDigits); // 5 digits -> S, else B
    const tail = series === "B" ? tailDigits.padStart(4, "0") : tailDigits; // keep S as 5 digits
    return `${series}/${mmYY}/${tail}`;
  };

  // Prefer backend’s canonical receipt if we have it already
  const displayReceipt = () => (transaction?.receiptNo?.trim()) || buildCanonicalReceipt();

  // ---------- actions ----------
  const handleSearch = async () => {
    setTransaction(null);
    setError("");
    if (!mmyy || !receiptNo) {
      setError("Please fill MMYY and Receipt Number.");
      return;
    }
    try {
      const res = await axios.get("https://www.lianfoong.com/api/pawn-transactions/search", {
        params: { mmyy: pad4(mmyy), receiptNo: onlyDigits(receiptNo) },
      });
      setTransaction(res.data);
      setConfirmCancel(null);
      setReconfirmCancel(null);
    } catch {
      setError("Transaction not found!");
    }
  };

  const onInitialConfirm = (ans) => {
    setConfirmCancel(ans === "yes");
    if (ans !== "yes") setReconfirmCancel(false);
  };

  const onReconfirm = async (ans) => {
    setReconfirmCancel(ans === "yes");
    if (ans !== "yes") return;

    try {
      setIsSubmitting(true);
      await axios.put(
        "https://www.lianfoong.com/api/pawn-transactions/cancel-redeem",
        {},
        { params: { receiptNo: displayReceipt() } }
      );
      alert("Redemption cancelled successfully.");
      navigate("/dashboard");
    } catch (e) {
      const msg = e?.response?.data || "Cancellation failed.";
      alert(msg);
      setIsSubmitting(false);
    }
  };

  // ---------- UI ----------
  return (
    <div className="renew-container">
      <h2 className="title">Cancel – Redeem</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(onlyDigits(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              receiptRef.current?.focus();
            }
          }}
          placeholder="MMYY"
          inputMode="numeric"
          maxLength={4}
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          ref={receiptRef}
          value={receiptNo}
          onChange={(e) => setReceiptNo(onlyDigits(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
          placeholder="0001 or 80001"
          inputMode="numeric"
        />
      </div>

      <button className="search-btn" onClick={handleSearch}>Search</button>
      {error && <p className="error-message">{error}</p>}

      {transaction && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Receipt:</strong> {displayReceipt()}</p>
            <p><strong>Items:</strong> {transaction.item}</p>
            <p><strong>Weight:</strong> {transaction.weight}</p>
            <p><strong>Amount:</strong> ${Number(transaction.amount).toFixed(2)}</p>
            <p><strong>Grant Date:</strong> {formatDateDDMMYYYY(transaction.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {formatDateDDMMYYYY(transaction.expiryDate)}</p>
            <p><strong>Redeemed?</strong> {transaction.redeemDate ? "Yes" : "No"}</p>
            {transaction.redeemDate && (
              <>
                <p><strong>Redeem Date:</strong> {formatDateDDMMYYYY(transaction.redeemDate)}</p>
                <p><strong>Redeem Amount:</strong> ${Number(transaction.redeemAmount).toFixed(2)}</p>
              </>
            )}
          </div>

          {confirmCancel === null && (
            <div className="alert-box">
              <p><strong>Are you sure you want to cancel the redemption for this ticket?</strong></p>
              <button onClick={() => onInitialConfirm("yes")}>Yes</button>
              <button onClick={() => onInitialConfirm("no")}>No</button>
            </div>
          )}

          {confirmCancel === true && reconfirmCancel === null && (
            <div className="alert-box">
              <p><strong>Please re-confirm:</strong></p>
              <button disabled={isSubmitting} onClick={() => onReconfirm("yes")}>
                {isSubmitting ? "Cancelling..." : "Yes"}
              </button>
              <button disabled={isSubmitting} onClick={() => onReconfirm("no")}>No</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
